import React from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  clearTeamDashboardContext,
  type TeamDashboardContext,
} from '../../../utils/coach/teamDashboardNavigation';
import {
  clearUploadBatch,
  labelForBatchSession,
  navigateToBatchSession,
  type UploadBatchContext,
} from '../../../utils/upload/uploadBatchNavigation';

interface CoachDashboardNavBarProps {
  teamContext: TeamDashboardContext | null;
  uploadBatch: UploadBatchContext | null;
  currentSessionId: string | null;
  userId: string;
  onDismissUploadBatch: () => void;
}

const CoachDashboardNavBar: React.FC<CoachDashboardNavBarProps> = ({
  teamContext,
  uploadBatch,
  currentSessionId,
  userId,
  onDismissUploadBatch,
}) => {
  const navigate = useNavigate();
  const showUploadSwitcher = Boolean(uploadBatch && uploadBatch.sessions.length > 1);
  const backLabel = teamContext
    ? `Back to ${teamContext.teamName} sessions`
    : 'Back to team sessions';

  const handleBackToTeam = () => {
    if (userId) clearUploadBatch(userId);
    onDismissUploadBatch();
    if (teamContext?.returnTo) {
      if (userId) clearTeamDashboardContext(userId);
      navigate(teamContext.returnTo);
    }
  };

  const handleDismissUploadBatch = () => {
    if (userId) clearUploadBatch(userId);
    onDismissUploadBatch();
  };

  if (!teamContext && !showUploadSwitcher) {
    return null;
  }

  return (
    <div
      className="mb-4 flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid="coach-dashboard-nav"
    >
      <div className="flex items-center gap-3 min-w-0">
        {teamContext ? (
          <button
            type="button"
            onClick={handleBackToTeam}
            className="inline-flex items-center gap-2 text-sm font-medium text-rowing-blue hover:text-rowing-navy"
            data-testid="back-to-team-sessions"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            {backLabel}
          </button>
        ) : (
          <span className="text-sm text-blue-900">Uploaded just now</span>
        )}
        {showUploadSwitcher && (
          <button
            type="button"
            onClick={handleDismissUploadBatch}
            className="inline-flex items-center rounded p-1 text-blue-700 hover:bg-blue-100 hover:text-blue-900"
            aria-label="Dismiss upload navigation"
            title="Dismiss upload shortcuts"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showUploadSwitcher && uploadBatch && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-blue-800">Jump to</span>
          {uploadBatch.sessions.map((session) => {
            const isActive = session.sessionId === currentSessionId;
            return (
              <button
                key={session.sessionId}
                type="button"
                title={labelForBatchSession(session)}
                onClick={() => {
                  if (isActive || !userId) return;
                  navigateToBatchSession(navigate, session.sessionId, uploadBatch, userId);
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'border border-blue-300 bg-white text-blue-900 hover:bg-blue-100'
                }`}
              >
                {session.sheetName}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachDashboardNavBar;
